import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  CircularProgress,
  Tooltip,
  InputAdornment,
  Checkbox,
  LinearProgress,
  Alert,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Add as AddIcon,
  Block as BlockIcon,
  Check as CheckIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  AutoFixHigh as AiIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { adminApi } from '../../lib/adminApi';
import { toast } from 'react-toastify';

// Glavni atributi koji ne idu kroz spec_value_mappings (sync ih uvijek emituje)
const ATTRIBUTES_WITHOUT_VALUE_MAPPING = new Set(['pa_brend', 'pa_garancija']);

interface UnmappedValue {
  target_attribute: string;
  unmapped_value: string;
  source_key: string;
  product_count: number;
}

interface MappedValue {
  id: number;
  target_attribute: string;
  original_value: string;
  normalized_value: string;
  created_at: string;
}

export default function AttributeValues() {
  const muiTheme = useTheme();
  const fullScreenDialog = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const [selectedAttribute, setSelectedAttribute] = useState<string>('pa_cpu');
  const [activeTab, setActiveTab] = useState(0); // 0 = nemapirane, 1 = mapirane
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState<UnmappedValue | null>(null);
  const [normalizedValue, setNormalizedValue] = useState('');

  // AI suggest state
  const [aiLoading, setAiLoading] = useState<string | null>(null);
  const [aiSuggestion, setAiSuggestion] = useState<{
    suggestion: string;
    confidence: 'high' | 'medium' | 'low';
    reasoning: string;
  } | null>(null);

  // Bulk selection state
  const [selectedValues, setSelectedValues] = useState<Set<string>>(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkSuggestions, setBulkSuggestions] = useState<Array<{
    value: string;
    suggestion: string;
    confidence: 'high' | 'medium' | 'low';
    reasoning: string;
    accepted: boolean;
  }>>([]);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(0);

  const queryClient = useQueryClient();

  // Dohvati glavne atribute (izvor: spec_key_mappings.target_attribute)
  const { data: mainAttributes = [] } = useQuery({
    queryKey: ['main-attributes'],
    queryFn: async () => {
      const { attributes } = await adminApi.mainAttributes.list();
      return attributes.filter(a => !ATTRIBUTES_WITHOUT_VALUE_MAPPING.has(a.slug));
    },
  });

  // Dohvati nemapirane vrijednosti
  const { data: unmappedValues, isLoading: loadingUnmapped, refetch: refetchUnmapped } = useQuery({
    queryKey: ['unmapped-values', selectedAttribute],
    queryFn: async () => {
      const data = await adminApi.specValueMappings.unmapped(selectedAttribute);
      return (data || []) as UnmappedValue[];
    },
  });

  // Dohvati mapirane vrijednosti
  const { data: mappedValues, isLoading: loadingMapped, refetch: refetchMapped } = useQuery({
    queryKey: ['mapped-values', selectedAttribute],
    queryFn: async () => {
      const data = await adminApi.specValueMappings.list(selectedAttribute);
      return (data || []) as MappedValue[];
    },
  });

  // Dodaj mapiranje
  const addMappingMutation = useMutation({
    mutationFn: async ({ originalValue, normalizedValue }: { originalValue: string; normalizedValue: string }) => {
      await adminApi.specValueMappings.create({
        target_attribute: selectedAttribute,
        original_value: originalValue,
        normalized_value: normalizedValue,
      });
    },
    onSuccess: () => {
      toast.success('Mapiranje dodano!');
      queryClient.invalidateQueries({ queryKey: ['unmapped-values'] });
      queryClient.invalidateQueries({ queryKey: ['mapped-values'] });
      setDialogOpen(false);
      setSelectedValue(null);
      setNormalizedValue('');
    },
    onError: (error: any) => {
      toast.error(`Greška: ${error.message}`);
    },
  });

  // Obriši mapiranje
  const deleteMappingMutation = useMutation({
    mutationFn: async (id: number) => {
      await adminApi.specValueMappings.remove(id);
    },
    onSuccess: () => {
      toast.success('Mapiranje obrisano!');
      queryClient.invalidateQueries({ queryKey: ['mapped-values'] });
      queryClient.invalidateQueries({ queryKey: ['unmapped-values'] });
    },
    onError: (error: any) => {
      toast.error(`Greška: ${error.message}`);
    },
  });

  // AI suggest funkcija
  const handleAiSuggest = async (value: UnmappedValue) => {
    setAiLoading(value.unmapped_value);
    setAiSuggestion(null);

    try {
      // Pripremi postojeća mapiranja za kontekst
      const existingMappings = (mappedValues || []).map(m => ({
        original: m.original_value,
        normalized: m.normalized_value,
      }));

      const response = await fetch('/api/ai/suggest-mapping', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          unmappedValue: value.unmapped_value,
          targetAttribute: selectedAttribute,
          existingMappings,
        }),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'AI suggestion failed');
      }

      setAiSuggestion({
        suggestion: data.suggestion || '',
        confidence: data.confidence || 'medium',
        reasoning: data.reasoning || '',
      });

      // Otvori dialog sa AI sugestijom
      setSelectedValue(value);
      setNormalizedValue(data.suggestion || '');
      setDialogOpen(true);

      toast.success(`AI predlaže: "${data.suggestion || 'IGNORIŠI'}" (${data.confidence})`);
    } catch (err: any) {
      toast.error(`AI greška: ${err.message}`);
      setAiSuggestion(null);
    } finally {
      setAiLoading(null);
    }
  };

  // Bulk AI suggest funkcija
  const handleBulkAiSuggest = async () => {
    if (selectedValues.size === 0) {
      toast.warning('Odaberi vrijednosti za AI analizu');
      return;
    }

    setBulkLoading(true);
    setBulkProgress(0);
    setBulkSuggestions([]);

    try {
      const existingMappings = (mappedValues || []).map(m => ({
        original: m.original_value,
        normalized: m.normalized_value,
      }));

      const valuesToProcess = Array.from(selectedValues);

      // Process in batches of 20 to avoid token limits
      const BATCH_SIZE = 20;
      const allSuggestions: typeof bulkSuggestions = [];

      for (let i = 0; i < valuesToProcess.length; i += BATCH_SIZE) {
        const batch = valuesToProcess.slice(i, i + BATCH_SIZE);
        setBulkProgress(Math.round((i / valuesToProcess.length) * 100));

        const response = await fetch('/api/ai/suggest-mapping', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            unmappedValues: batch,
            targetAttribute: selectedAttribute,
            existingMappings,
          }),
        });

        const data = await response.json();

        if (!data.success) {
          throw new Error(data.error || 'Bulk AI suggestion failed');
        }

        const batchSuggestions = (data.suggestions || []).map((s: any) => ({
          value: s.value,
          suggestion: s.suggestion || '',
          confidence: s.confidence || 'medium',
          reasoning: s.reasoning || '',
          accepted: true, // Default accepted
        }));

        allSuggestions.push(...batchSuggestions);
      }

      setBulkProgress(100);
      setBulkSuggestions(allSuggestions);
      setBulkDialogOpen(true);

      toast.success(`AI generirao ${allSuggestions.length} sugestija`);
    } catch (err: any) {
      toast.error(`Bulk AI greška: ${err.message}`);
    } finally {
      setBulkLoading(false);
    }
  };

  // Bulk save funkcija
  const handleBulkSave = async () => {
    const toSave = bulkSuggestions.filter(s => s.accepted);

    if (toSave.length === 0) {
      toast.warning('Nema prihvaćenih sugestija za spremanje');
      return;
    }

    setBulkLoading(true);

    try {
      // Insert all mappings one by one (create endpoint takes a single row;
      // good enough for small bulk-accept batches). Collect errors per row.
      for (const s of toSave) {
        await adminApi.specValueMappings.create({
          target_attribute: selectedAttribute,
          original_value: s.value,
          normalized_value: s.suggestion,
        });
      }

      toast.success(`Spremljeno ${toSave.length} mapiranja!`);
      setBulkDialogOpen(false);
      setBulkSuggestions([]);
      setSelectedValues(new Set());
      queryClient.invalidateQueries({ queryKey: ['unmapped-values'] });
      queryClient.invalidateQueries({ queryKey: ['mapped-values'] });
    } catch (err: any) {
      toast.error(`Greška: ${err.message}`);
    } finally {
      setBulkLoading(false);
    }
  };

  // Toggle selection
  const toggleSelection = (value: string) => {
    setSelectedValues(prev => {
      const newSet = new Set(prev);
      if (newSet.has(value)) {
        newSet.delete(value);
      } else {
        newSet.add(value);
      }
      return newSet;
    });
  };

  // Select all visible
  const toggleSelectAll = () => {
    if (selectedValues.size === paginatedUnmapped.length) {
      setSelectedValues(new Set());
    } else {
      setSelectedValues(new Set(paginatedUnmapped.map(v => v.unmapped_value)));
    }
  };

  // Filtrirane vrijednosti
  const filteredUnmapped = useMemo(() => {
    if (!unmappedValues) return [];
    return unmappedValues.filter(v =>
      v.unmapped_value.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [unmappedValues, searchQuery]);

  const filteredMapped = useMemo(() => {
    if (!mappedValues) return [];
    return mappedValues.filter(v =>
      v.original_value.toLowerCase().includes(searchQuery.toLowerCase()) ||
      v.normalized_value.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [mappedValues, searchQuery]);

  // Paginirane vrijednosti
  const paginatedUnmapped = filteredUnmapped.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  const paginatedMapped = filteredMapped.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  const handleOpenDialog = (value: UnmappedValue, action: 'map' | 'ignore') => {
    setSelectedValue(value);
    setNormalizedValue(action === 'ignore' ? '' : value.unmapped_value);
    setAiSuggestion(null); // Clear AI suggestion when opening manually
    setDialogOpen(true);
  };

  const handleSaveMapping = () => {
    if (!selectedValue) return;
    addMappingMutation.mutate({
      originalValue: selectedValue.unmapped_value,
      normalizedValue: normalizedValue,
    });
  };

  const currentAttribute = mainAttributes.find(a => a.slug === selectedAttribute);

  return (
    <Box sx={{ p: { xs: 0, sm: 2, md: 3 }, minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* Header */}
      <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3, borderRadius: 2 }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          Upravljanje Vrijednostima Atributa
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Mapiraj vrijednosti iz feedova na standardizirane vrijednosti za WooCommerce atribute.
          Samo mapirane vrijednosti će se upisivati u glavne atribute (pa_cpu, pa_ram, itd.)
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Atribut</InputLabel>
            <Select
              value={currentAttribute ? selectedAttribute : ''}
              label="Atribut"
              onChange={(e) => {
                setSelectedAttribute(e.target.value);
                setPage(0);
              }}
            >
              {mainAttributes.map(attr => (
                <MenuItem key={attr.slug} value={attr.slug}>
                  {attr.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            placeholder="Pretraži..."
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 200, flex: 1 }}
          />

          <Button
            startIcon={<RefreshIcon />}
            onClick={() => {
              refetchUnmapped();
              refetchMapped();
            }}
          >
            Osvježi
          </Button>

          {/* Bulk actions */}
          {selectedValues.size > 0 && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', ml: 2, pl: 2, borderLeft: '1px solid #ddd' }}>
              <Chip
                label={`${selectedValues.size} odabrano`}
                color="primary"
                size="small"
              />
              <Button
                variant="contained"
                color="secondary"
                startIcon={bulkLoading ? <CircularProgress size={16} color="inherit" /> : <AiIcon />}
                onClick={handleBulkAiSuggest}
                disabled={bulkLoading}
                size="small"
              >
                AI Bulk ({selectedValues.size})
              </Button>
              <Button
                size="small"
                onClick={() => setSelectedValues(new Set())}
              >
                Poništi
              </Button>
            </Box>
          )}
        </Box>

        {/* Bulk progress */}
        {bulkLoading && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress variant="determinate" value={bulkProgress} />
            <Typography variant="caption" color="text.secondary">
              Obrađujem... {bulkProgress}%
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Tabs */}
      <Paper sx={{ borderRadius: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => {
            setActiveTab(newValue);
            setPage(0);
          }}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Nemapirane
                <Chip
                  label={filteredUnmapped.length}
                  size="small"
                  color="error"
                  sx={{ height: 20, fontSize: 12 }}
                />
              </Box>
            }
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Mapirane
                <Chip
                  label={filteredMapped.length}
                  size="small"
                  color="success"
                  sx={{ height: 20, fontSize: 12 }}
                />
              </Box>
            }
          />
        </Tabs>

        {/* Nemapirane vrijednosti */}
        {activeTab === 0 && (
          <TableContainer>
            {loadingUnmapped ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : filteredUnmapped.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <CheckIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  Sve vrijednosti su mapirane!
                </Typography>
              </Box>
            ) : (
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#fafafa' }}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={selectedValues.size > 0 && selectedValues.size < paginatedUnmapped.length}
                        checked={paginatedUnmapped.length > 0 && selectedValues.size === paginatedUnmapped.length}
                        onChange={toggleSelectAll}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Originalna Vrijednost</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Source Key</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">Proizvoda</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Akcije</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedUnmapped.map((value, index) => (
                    <TableRow
                      key={index}
                      hover
                      selected={selectedValues.has(value.unmapped_value)}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedValues.has(value.unmapped_value)}
                          onChange={() => toggleSelection(value.unmapped_value)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontFamily: 'monospace', fontSize: 13 }}>
                          {value.unmapped_value}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={value.source_key} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={value.product_count}
                          size="small"
                          color={value.product_count > 10 ? 'warning' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="AI Predloži">
                          <IconButton
                            color="secondary"
                            size="small"
                            onClick={() => handleAiSuggest(value)}
                            disabled={aiLoading === value.unmapped_value}
                          >
                            {aiLoading === value.unmapped_value ? (
                              <CircularProgress size={18} />
                            ) : (
                              <AiIcon />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Mapiraj na vrijednost">
                          <IconButton
                            color="primary"
                            size="small"
                            onClick={() => handleOpenDialog(value, 'map')}
                          >
                            <AddIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Ignoriši (ne upisuj)">
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => handleOpenDialog(value, 'ignore')}
                          >
                            <BlockIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TableContainer>
        )}

        {/* Mapirane vrijednosti */}
        {activeTab === 1 && (
          <TableContainer>
            {loadingMapped ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Table sx={{ minWidth: 550 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#fafafa' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Originalna Vrijednost</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Normalizirana Vrijednost</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Akcije</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedMapped.map((value) => (
                    <TableRow key={value.id} hover>
                      <TableCell>
                        <Typography sx={{ fontFamily: 'monospace', fontSize: 13 }}>
                          {value.original_value}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {value.normalized_value ? (
                          <Chip
                            label={value.normalized_value}
                            color="success"
                            size="small"
                          />
                        ) : (
                          <Chip
                            label="IGNORIRANO"
                            color="error"
                            size="small"
                            icon={<BlockIcon />}
                          />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          color="error"
                          onClick={() => {
                            if (confirm('Obrisati ovo mapiranje?')) {
                              deleteMappingMutation.mutate(value.id);
                            }
                          }}
                        >
                          Obriši
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TableContainer>
        )}

        <TablePagination
          component="div"
          count={activeTab === 0 ? filteredUnmapped.length : filteredMapped.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </Paper>

      {/* Dialog za mapiranje */}
      <Dialog open={dialogOpen} onClose={() => {
        setDialogOpen(false);
        setAiSuggestion(null);
      }} maxWidth="sm" fullWidth>
        <DialogTitle>
          {normalizedValue === '' ? 'Ignoriši Vrijednost' : 'Mapiraj Vrijednost'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Atribut: <strong>{currentAttribute?.label}</strong>
            </Typography>

            <TextField
              fullWidth
              label="Originalna vrijednost (iz feeda)"
              value={selectedValue?.unmapped_value || ''}
              disabled
              sx={{ mb: 3 }}
            />

            {aiSuggestion && (
              <Paper
                sx={{
                  p: 2,
                  mb: 3,
                  bgcolor: aiSuggestion.confidence === 'high' ? 'success.50' :
                           aiSuggestion.confidence === 'medium' ? 'warning.50' : 'grey.100',
                  border: 1,
                  borderColor: aiSuggestion.confidence === 'high' ? 'success.main' :
                               aiSuggestion.confidence === 'medium' ? 'warning.main' : 'grey.400',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <AiIcon color="secondary" fontSize="small" />
                  <Typography variant="subtitle2">
                    AI Sugestija
                    <Chip
                      label={aiSuggestion.confidence}
                      size="small"
                      color={aiSuggestion.confidence === 'high' ? 'success' :
                             aiSuggestion.confidence === 'medium' ? 'warning' : 'default'}
                      sx={{ ml: 1, height: 20, fontSize: 11 }}
                    />
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                  {aiSuggestion.reasoning}
                </Typography>
              </Paper>
            )}

            <TextField
              fullWidth
              label="Normalizirana vrijednost (za WooCommerce)"
              value={normalizedValue}
              onChange={(e) => setNormalizedValue(e.target.value)}
              placeholder={normalizedValue === '' ? 'Ostavi prazno za ignoriranje' : ''}
              helperText={
                normalizedValue === ''
                  ? 'Prazna vrijednost = ignoriši (neće se upisati u atribut)'
                  : 'Ova vrijednost će se upisati u WooCommerce'
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Odustani</Button>
          <Button
            variant="contained"
            onClick={handleSaveMapping}
            disabled={addMappingMutation.isPending}
            color={normalizedValue === '' ? 'error' : 'primary'}
          >
            {normalizedValue === '' ? 'Ignoriši' : 'Spremi'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Review Dialog */}
      <Dialog
        open={bulkDialogOpen}
        onClose={() => setBulkDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={fullScreenDialog}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AiIcon color="secondary" />
            AI Bulk Sugestije
            <Chip
              label={`${bulkSuggestions.filter(s => s.accepted).length}/${bulkSuggestions.length} prihvaćeno`}
              size="small"
              color="primary"
              sx={{ ml: 1 }}
            />
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Pregledaj AI sugestije i odznači one koje ne želiš spremiti. Klikni "Spremi Sve" za bulk unos.
          </Alert>

          <TableContainer sx={{ maxHeight: 400 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={bulkSuggestions.every(s => s.accepted)}
                      indeterminate={bulkSuggestions.some(s => s.accepted) && !bulkSuggestions.every(s => s.accepted)}
                      onChange={(e) => {
                        setBulkSuggestions(prev =>
                          prev.map(s => ({ ...s, accepted: e.target.checked }))
                        );
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Original</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>AI Sugestija</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Confidence</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bulkSuggestions.map((item, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      bgcolor: !item.accepted ? 'action.disabledBackground' : 'inherit',
                      opacity: item.accepted ? 1 : 0.6,
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={item.accepted}
                        onChange={() => {
                          setBulkSuggestions(prev =>
                            prev.map((s, i) =>
                              i === index ? { ...s, accepted: !s.accepted } : s
                            )
                          );
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                        {item.value}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {item.suggestion ? (
                        <Chip label={item.suggestion} size="small" color="success" />
                      ) : (
                        <Chip label="IGNORIŠI" size="small" color="error" icon={<BlockIcon />} />
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={item.reasoning}>
                        <Chip
                          label={item.confidence}
                          size="small"
                          color={
                            item.confidence === 'high' ? 'success' :
                            item.confidence === 'medium' ? 'warning' : 'default'
                          }
                        />
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDialogOpen(false)}>Odustani</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleBulkSave}
            disabled={bulkLoading || bulkSuggestions.filter(s => s.accepted).length === 0}
            startIcon={bulkLoading ? <CircularProgress size={16} /> : <CheckIcon />}
          >
            Spremi Sve ({bulkSuggestions.filter(s => s.accepted).length})
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
